import React, { useCallback, useEffect, useRef, useState } from 'react';

import { isWeb } from '@tamagui/core';
import { Animated, Easing, PanResponder, Platform, Text as RNText } from 'react-native';
import type { LayoutChangeEvent, PanResponderInstance } from 'react-native';

import { Box } from '../Box';
import { ChevronBackButton, ChevronForwardButton } from '../Button';
import { Image } from '../Image/Image';
import { Text } from '../Text/Text';
import { PhotoCarouselDots } from './PhotoCarouselDots';

/**
 * Props for the image carousel component.
 */
export interface PhotoCarouselProps {
  /**
   * An array of image URLs.
   */
  photos: {
    /**
     * The URL of the image.
     */
    url: string;
    /**
     * The alt text for the image.
     */
    alt: string;
  }[];
  /**
   * These would be the optional corner button icons.
   * Example:
   *  <PhotoCarousel {...args}>
   *     <Box position="absolute" top="$10x" left="$10x">
   *       <ChevronBackButton
   *         size="medium"
   *         analyticsName=""
   *         onPress={() => alert('Top Left Button Pressed')}
   *       />
   *     </Box>
   *   </PhotoCarousel>
   */
  children?: React.ReactNode;
  /**
   * The aria-label for the carousel.
   */
  ariaLabel?: string;
  /**
   * Hide the navigation buttons. Default is to show them.
   */
  hideNavigation?: boolean;
  /**
   * The aspect ratio of the carousel. Default is 16:9.
   */
  aspectRatio?: PhotoCarouselAspectRatios;
  /**
   * The height of the carousel. Default is 100%.
   */
  height?: number | string;
  /**
   * Whether the carousel should have rounded corners.
   */
  isRounded?: boolean;
}

const DEFAULT_ASPECT_RATIO = 16 / 9;
const SQUARE_ASPECT_RATIO = 1 / 1;

export enum PhotoCarouselAspectRatios {
  DEFAULT = DEFAULT_ASPECT_RATIO,
  SQUARE = SQUARE_ASPECT_RATIO,
}

/**
 * Image carousel base component.
 * @param {PhotoCarouselProps} props - The props for the component.
 * @returns {React.JSX.Element} The rendered component.
 */
export const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  photos,
  children,
  ariaLabel,
  hideNavigation,
  aspectRatio = PhotoCarouselAspectRatios.DEFAULT,
  height = '100%',
  isRounded = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [panResponder, setPanResponder] = useState<PanResponderInstance | null>(null);
  const offsetX = useRef(new Animated.Value(0)).current;
  const currentOffset = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const navigationMessagePosition = useRef(new Animated.Value(0)).current;
  // We want to show navigation message, when the container is focused
  // and the user is using a keyboard to navigate through the carousel
  // and when hideNavigation is true
  const [showNavigationMessage, setShowNavigationMessage] = useState(false);
  const useNativeDriver = Platform.OS !== 'web';
  /**
   * Handles the layout change event.
   * @param {LayoutChangeEvent} event - The layout change event.
   */
  const onLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  useEffect(() => {
    Animated.timing(navigationMessagePosition, {
      toValue: showNavigationMessage ? -65 : 0,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver,
    }).start();
  }, [navigationMessagePosition, showNavigationMessage, useNativeDriver]);

  useEffect(() => {
    const responder = PanResponder.create({
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        offsetX.setValue(currentOffset.current);
      },
      onPanResponderMove: (evt, gestureState) => {
        offsetX.setValue(currentOffset.current + gestureState.dx);
      },
      onPanResponderRelease: (evt, gestureState) => {
        // Swiping a third of the screen width will move to the next image
        const SWIPE_DISTANCE_THRESHOLD = 7;
        const threshold = containerWidth / SWIPE_DISTANCE_THRESHOLD;
        let newIndex = currentImageIndex;

        if (gestureState.dx > threshold) {
          newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : currentImageIndex;
        } else if (gestureState.dx < -threshold) {
          newIndex =
            currentImageIndex < photos.length - 1 ? currentImageIndex + 1 : currentImageIndex;
        }

        if (newIndex !== currentImageIndex) {
          Animated.timing(offsetX, {
            toValue: -newIndex * containerWidth,
            duration: 300,
            easing: Easing.linear,
            useNativeDriver,
          }).start(() => {
            setCurrentImageIndex(newIndex);
            currentOffset.current = -newIndex * containerWidth;
          });
        } else {
          Animated.spring(offsetX, {
            toValue: currentOffset.current,
            useNativeDriver,
          }).start();
        }
      },
    });
    setPanResponder(responder);
  }, [containerWidth, photos.length, offsetX, currentImageIndex, useNativeDriver]);

  useEffect(() => {
    offsetX.setValue(-currentImageIndex * containerWidth);
    currentOffset.current = -currentImageIndex * containerWidth;
  }, [currentImageIndex, containerWidth, offsetX]);

  /**
   * Renders the photos in the carousel.
   * This function will only render the images that are within the window size.
   * So x images before and x images after the current image.
   * @returns {React.JSX.Element[]} The rendered images.
   */
  const renderImages = () => {
    return photos.map((photo, index) => {
      const windowSize = 2;

      if (index >= currentImageIndex - windowSize && index <= currentImageIndex + windowSize) {
        return (
          <Box
            key={index}
            width={containerWidth}
            height="100%"
            aria-hidden={index !== currentImageIndex}
          >
            <Image
              source={{ uri: photo.url }}
              alt={photo.alt || `Image ${index + 1}`}
              width="100%"
              height="100%"
            />
          </Box>
        );
      }
      return <Box key={index} width={containerWidth} height="100%" aria-hidden={true} />;
    });
  };

  const handlePrev = useCallback(() => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  }, [currentImageIndex]);

  const handleNext = useCallback(() => {
    if (currentImageIndex < photos.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  }, [currentImageIndex, photos.length]);

  useEffect(() => {
    if (!isWeb) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
      if (showNavigationMessage) {
        setShowNavigationMessage(false);
      }
    };
    const carouselContainer = carouselRef.current;

    carouselContainer?.addEventListener('keydown', handleKeyDown);
    return () => {
      carouselContainer?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNext, handlePrev, showNavigationMessage]);

  const atStart = currentImageIndex === 0;
  const atEnd = currentImageIndex === photos.length - 1;

  const handleOnFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    // setShowNavigation only when we tab in and not click
    // relatedTarget is null when we click, but exists when we tab
    if (document.activeElement === carouselRef.current && event.relatedTarget !== null) {
      setShowNavigationMessage(true);
      setTimeout(() => {
        setShowNavigationMessage(false);
      }, 3000);
    }
  };

  return (
    <Box
      role="region"
      aria-label={ariaLabel || 'Photo Carousel'}
      tabIndex={0}
      ref={carouselRef}
      onFocus={handleOnFocus}
      data-testid="photo-carousel"
    >
      {/* Here for screen readers and to explain functionality */}
      <RNText
        accessibilityRole="text"
        accessible={false}
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
      >
        This is an photo carousel of the property. Swipe left or right to navigate through the
        photos.{' '}
        {!hideNavigation &&
          'Alternatively, use the next and previous buttons to move between photos.'}
      </RNText>
      <Box width="100%" onLayout={onLayout} {...(panResponder && panResponder.panHandlers)}>
        <Box
          aspectRatio={aspectRatio}
          width="100%"
          position="relative"
          overflow="hidden"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          height={height}
          borderTopLeftRadius={isRounded ? '$16x' : undefined}
          borderTopRightRadius={isRounded ? '$16x' : undefined}
        >
          <Animated.View
            style={{
              flexDirection: 'row',
              width: containerWidth * photos.length,
              height: '100%',
              transform: [{ translateX: offsetX }],
            }}
          >
            {renderImages()}
          </Animated.View>
          {isWeb && !hideNavigation ? (
            <>
              {!atStart && (
                <ChevronBackButton
                  size="medium"
                  position="absolute"
                  top="50%"
                  left="$10x"
                  analyticsName=""
                  onPress={handlePrev}
                  aria-label="Previous photo"
                  data-testid="chevron-back-button"
                />
              )}
              {!atEnd && (
                <ChevronForwardButton
                  size="medium"
                  position="absolute"
                  top="50%"
                  right="$10x"
                  analyticsName=""
                  onPress={handleNext}
                  aria-label="Next photo"
                  data-testid="chevron-forward-button"
                />
              )}
            </>
          ) : null}
          {children}
          <Box
            position="absolute"
            bottom="$10x"
            width={containerWidth}
            alignItems="center"
            flexDirection="row"
            justifyContent="center"
            data-testid="photo-carousel-dots"
          >
            <PhotoCarouselDots length={photos.length} currentIndex={currentImageIndex} />
          </Box>
          {hideNavigation ? (
            <Animated.View
              style={{
                position: 'absolute',
                bottom: -30,
                left: 0,
                right: 0,
                justifyContent: 'center',
                flexDirection: 'row',
                transform: [{ translateY: navigationMessagePosition }],
              }}
            >
              <Text
                backgroundColor="$backgroundInversePrimary"
                color="$backgroundPrimary"
                paddingHorizontal="$4x"
                paddingVertical="$2x"
                borderRadius="$10x"
                typography="$bodySmall"
                opacity={0.7}
              >
                Use arrow keys to navigate
              </Text>
            </Animated.View>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
